import React from 'react';
import {
  BLOCKS,
  INLINES,
} from '@contentful/rich-text-types';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import _get from 'lodash/get';
import generalHelper from '../../utils/general';
import OlList from './OlList';
import UlList from './UlList';
// Note: There is a cycle because the Step can render anything inside of it.
// eslint-disable-next-line import/no-cycle
import Step from './Step';
import Video from './Video';
import Quote from './Quote';
import Tip from './Tip';

const getHref = value => value.toLowerCase().replaceAll(' ', '-');
let WEBSITE_URL = '';

if (!generalHelper.isWindowUndefined()) {
  const { location: { origin: websiteUrl } } = window;
  WEBSITE_URL = websiteUrl;
}


const renderOptionsDefault = {
  renderNode: {
    [BLOCKS.EMBEDDED_ASSET]: (node) => {
      const target = _get(node, 'data.target', {});
      const title = _get(target, 'title', '');
      return (
        <GatsbyImage
          image={getImage(target)}
          alt={title}
        />
      );
    },
    [INLINES.EMBEDDED_ENTRY]: (node) => {
      const ENTRY_TYPES = {
        IMAGE_COLUMN: 'ContentfulElementImageColumn',
      };
      const target = _get(node, 'data.target', {});
      const type = _get(target, '__typename', '');

      switch (type) {
        case ENTRY_TYPES.IMAGE_COLUMN: {
          const { left: isLeft, image, title } = target;
          return (
            <div className={`${ENTRY_TYPES.IMAGE_COLUMN} ${!isLeft ? 'right' : ''}`}>
              <GatsbyImage
                image={getImage(image)}
                alt={title}
              />
            </div>
          );
        }
        default:
          return '';
      }
    },
    [BLOCKS.EMBEDDED_ENTRY]: (node) => {
      const ENTRY_TYPES = {
        BUTTON: 'ContentfulElementButton',
        TIP: 'ContentfulElementTip',
        QUOTE: 'ContentfulElementQuote',
        IMAGE_COLUMN: 'ContentfulElementImageColumn',
        STEP: 'ContentfulElementStep',
        VIDEO: 'ContentfulElementVideo',
      };
      const target = _get(node, 'data.target', {});
      const type = _get(target, '__typename', '');

      switch (type) {
        case ENTRY_TYPES.BUTTON: {
          const { buttonText, link } = target;
          return (
            <div className={ENTRY_TYPES.BUTTON}>
              <a
                rel="noopener noreferrer"
                target="_blank"
                href={link}
              >
                {buttonText}
              </a>
            </div>
          );
        }
        case ENTRY_TYPES.TIP: {
          const { text } = target;
          return (
            <Tip
              text={text.text}
              className={ENTRY_TYPES.TIP}
            />
          );
        }
        case ENTRY_TYPES.QUOTE: {
          const {
            text,
            author,
            company,
            companyUrl,
            role
          } = target;
          return (
            <Quote
              prefix={ENTRY_TYPES.QUOTE}
              text={text.text}
              author={author}
              companyName={company}
              companyUrl={companyUrl}
              role={role}
            />
          );
        }
        case ENTRY_TYPES.STEP: {
          const {
            number,
            content,
          } = target;
          return (
            <Step
              number={number}
              content={content}
            />
          );
        }
        case ENTRY_TYPES.VIDEO: {
          const {
            url,
          } = target;

          return (
            <Video url={url} />
          );
        }
        default:
          return '';
      }
    },
    [BLOCKS.OL_LIST]: (node, children) => (
      <OlList items={children} />
    ),
    [BLOCKS.UL_LIST]: (node, children) => (
      <UlList items={children} />
    ),
    [BLOCKS.HEADING_2]: (node, children) => {
      const props = {};
      const value = _get(node, 'content[0].value', '');
      if (value) {
        props.id = getHref(value);
      }

      return (
        <h2 {...props}>{children}</h2>
      );
    },
    [BLOCKS.PARAGRAPH]: (node, children) => <p>{children}</p>,
    [BLOCKS.LIST_ITEM]: (node, children) => (<React.Fragment>{children}</React.Fragment>),
    [INLINES.HYPERLINK]: (node, children) => (
      <a
        href={_get(node, 'data.uri', '')}
        target={`${_get(node, 'data.uri', '').startsWith(WEBSITE_URL) ? '_self' : '_blank'}`}
        rel={`${_get(node, 'data.uri', '').startsWith(WEBSITE_URL) ? '' : 'noopener noreferrer'}`}
      >
        {children}
      </a>
    ),
  },
};

// To render the header with links we have to pass the whole "richText",
// however, to make it work we have to NOT render all the other content.
// That's the reason we have the different types with a function that returns "null".
const renderOptionsHeader = {
  renderNode: {
    [BLOCKS.HEADING_2]: (node, children) => {
      const value = _get(node, 'content[0].value', '');
      const valueForHref = getHref(value);

      return (
        <a href={`#${valueForHref}`}>{children}</a>
      );
    },
    [BLOCKS.HEADING_1]: () => null,
    [BLOCKS.HEADING_3]: () => null,
    [BLOCKS.HEADING_4]: () => null,
    [BLOCKS.HEADING_5]: () => null,
    [BLOCKS.HEADING_6]: () => null,
    [BLOCKS.PARAGRAPH]: () => null,
    [BLOCKS.UL_LIST]: () => null,
    [BLOCKS.OL_LIST]: () => null,
    [BLOCKS.LIST_ITEM]: () => null,
    [BLOCKS.EMBEDDED_ASSET]: () => null,
    [BLOCKS.EMBEDDED_ENTRY]: () => null,
    [BLOCKS.QUOTE]: () => null,
    [INLINES.EMBEDDED_ENTRY]: () => null,
  }
};

const renderOptionsForEmbeddedArticles = {
  renderNode: {
    ...renderOptionsDefault.renderNode,
    [INLINES.HYPERLINK]: (node, children) => (
      <a
        href={_get(node, 'data.uri', '')}
        target="_blank"
        rel="noopener noreferrer"
      >
        {children}
      </a>
    ),
  }
};


export {
  renderOptionsDefault,
  renderOptionsHeader,
  renderOptionsForEmbeddedArticles,
};
export default null;
